import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import type {
  SavedWorkResponse, SearchField, SearchRole, UserSearchDetail, UserSearchResponse, UserType,
} from './types';

export const isSearchable = (q: string, field: SearchField) => {
  const trimmed = q.trim();

  if (field === 'id') return /^\d+$/.test(trimmed);

  return trimmed.length > 0;
};

interface UseUserSearchParams {
  exact: boolean;
  field: SearchField;
  perType: number;
  q: string;
  role: SearchRole;
}

export const useUserSearch = ({
  exact, field, perType, q, role,
}: UseUserSearchParams) => useQuery({
  enabled: isSearchable(q, field),
  keepPreviousData: true,
  queryFn: () => axios
    .get<UserSearchResponse>(Routes.plato_api_sysadmin_user_search_path({
    exact, field, per_type: perType, q: q.trim(), role,
  }))
    .then(res => res.data),
  queryKey: [QueryKeys.UserSearch, q.trim(), field, role, exact, perType],
});

export const useUserDetail = (userType: UserType, id: string) => useQuery({
  queryFn: () => axios
    .get<{ data: UserSearchDetail }>(Routes.plato_api_sysadmin_user_search_detail_path(userType, id))
    .then(res => res.data.data),
  queryKey: [QueryKeys.UserSearchDetail, userType, id],
});

export const useStafferSavedWork = (id: number, enabled: boolean) => useQuery({
  enabled,
  queryFn: () => axios
    .get<{ data: SavedWorkResponse }>(Routes.plato_api_sysadmin_user_search_saved_work_path(id))
    .then(res => res.data.data),
  queryKey: [QueryKeys.UserSearchSavedWork, id],
});

export const useDisconnectGoogle = (userType: UserType, id: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => axios
      .post<{ data: UserSearchDetail }>(Routes.plato_api_sysadmin_user_search_disconnect_google_path(userType, id))
      .then(res => res.data.data),
    onSuccess: (detail) => {
      queryClient.setQueryData([QueryKeys.UserSearchDetail, userType, String(id)], detail);
      // Other users' conflict callouts point at this link, so refresh every open detail.
      queryClient.invalidateQueries({ queryKey: [QueryKeys.UserSearchDetail] });
    },
  });
};
